'use client';

import { useState } from 'react';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import ContactModal from '../ui/ContactModal';
import CTAButton from '../ui/CTAButton';
import { trackEvent } from '@/lib/analytics';
import { useInView } from '@/lib/useInView';

export default function XIndex() {
  const pathname = usePathname();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { ref } = useInView();
  
  // Refs for animate-in-view
  const imageRef = useInView<HTMLDivElement>();
  const headerRef = useInView<HTMLDivElement>();
  const bodyRef = useInView<HTMLParagraphElement>();
  const buttonRef = useInView<HTMLDivElement>();

  return (
    <>
      {/* <section ref={ref} className="x-index pb-[60px] md:pb-[100px] pt-[100px] bg-[#FDFEF6] global-padding animate-in-view"> */}
      <section ref={ref} className="x-index bg-[#FDFEF6] global-padding animate-in-view">
        <div>
          {/* Main Content Grid */}
          <div className="x-index__grid">
            {/* Left Side - Image */}
            <div ref={imageRef.ref} className="x-index__image animate-in-view">
              <Image
                src="/images/xindex-picture.png"
                alt="X Index Report"
                fill
                className="object-cover"
                sizes="(min-width: 768px) 60vw, 100vw"
                priority
              />
            </div>

            {/* Right Side - Content */}
            <div className="space-y-6 self-center">
              {/* Heading with Badge */}
              <div ref={headerRef.ref} className="flex items-start gap-3 animate-in-view">
                <h2 className="heading-3">
                  X Index
                </h2>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="34"
                  height="38"
                  viewBox="0 0 34 38"
                  fill="none"
                  aria-hidden="true"
                  focusable="false"
                  className="mt-1"
                >
                  <path
                    className="xindex-asterisk-path"
                    d="M19.3664 0.766617C19.6576 0.757222 19.9327 0.896419 20.093 1.16212C20.2435 1.41183 20.2638 1.7205 20.2033 2.02931L20.2024 2.03419C19.1879 6.93746 18.5953 11.7176 18.2268 16.4043C21.8964 14.1806 25.5797 11.9118 29.4651 10.0235L29.511 10.001L29.5598 9.98927C29.6151 9.97618 29.7211 9.92489 29.9055 9.81252C30.0621 9.7171 30.2785 9.5769 30.4758 9.47169C30.6669 9.36979 30.9284 9.24986 31.2014 9.24904C31.3499 9.24864 31.5103 9.28356 31.6594 9.37892C31.7704 9.44994 31.8575 9.5438 31.925 9.64943L31.9865 9.7588L31.9953 9.77541L32.0022 9.79298C32.0819 9.99748 32.0885 10.2296 32.0324 10.4365C31.9758 10.6454 31.8401 10.8764 31.5862 11.0059L31.5774 11.0098L31.5696 11.0137C28.0539 12.63 24.711 14.5831 21.3791 16.5957C24.7508 16.275 28.16 16.16 31.5559 16.8027H31.5549C32.0267 16.879 32.2676 17.2555 32.3186 17.6045C32.3675 17.9396 32.2619 18.3354 31.9651 18.5918L31.9367 18.6172L31.9045 18.6367C31.6359 18.801 31.3158 18.8321 31.0403 18.8262C30.7553 18.8201 30.4473 18.7719 30.1594 18.7168C30.0139 18.689 29.8676 18.6581 29.7278 18.6289C29.5863 18.5994 29.4514 18.5711 29.3225 18.5469C29.0577 18.4971 28.8503 18.4697 28.7004 18.4736L28.6701 18.4746L28.6389 18.4717C25.4984 18.1329 22.3525 18.5333 19.1535 18.8848C21.9986 22.084 24.9153 25.2687 27.5305 28.7041L28.0852 29.4434L28.1155 29.4844L28.136 29.5303C28.1595 29.5835 28.23 29.6798 28.3742 29.8408C28.4967 29.9776 28.6749 30.164 28.8147 30.3389C28.9507 30.5091 29.1154 30.7445 29.1682 31.0117C29.1968 31.1567 29.1943 31.3201 29.1311 31.4844C29.0682 31.6478 28.958 31.7787 28.8254 31.8809L28.8117 31.8916L28.7961 31.9014C28.6108 32.0224 28.3836 32.0763 28.1682 32.0606C27.95 32.0446 27.6979 31.952 27.5246 31.7246L27.5198 31.7178L27.5149 31.7119C24.6431 27.6524 21.3004 23.9815 17.969 20.2529C17.8065 23.1895 17.7147 26.0927 17.6457 28.9697C17.6223 30.8512 17.5739 32.7243 17.6125 34.5459C17.6268 34.7999 17.6452 35.1292 17.635 35.458C17.6248 35.7868 17.5861 36.1583 17.4631 36.4736L17.4621 36.4746C17.3811 36.6812 17.2512 36.8975 17.0354 37.0488C16.8094 37.2073 16.5392 37.26 16.2531 37.2217L16.0901 37.2002L15.9778 37.0791C15.8231 36.9131 15.7359 36.6924 15.6809 36.4922C15.623 36.2818 15.5858 36.0382 15.5608 35.7813C15.5107 35.2669 15.5067 34.6443 15.5188 34.0147C15.5437 32.7178 15.6332 31.4242 15.5871 30.7061L15.5862 30.6885V30.6709C15.6259 27.1145 15.7778 23.4953 16.0119 19.834C15.9284 19.8837 15.8457 19.9347 15.7619 19.9844L15.758 19.9863C12.5009 21.877 9.21455 23.5934 6.07737 25.6074C5.74314 25.8522 5.39377 26.0891 5.05295 26.3213C4.70538 26.5581 4.36508 26.7925 4.03928 27.0381C3.4502 27.4823 2.92903 27.9509 2.5344 28.5059C2.58262 28.745 2.53175 28.9795 2.42893 29.1631C2.2914 29.4086 2.0148 29.6465 1.65647 29.6465C1.34565 29.6465 1.09314 29.466 0.93772 29.2022L0.933814 29.1944L0.929908 29.1875C0.65949 28.6878 0.75369 28.1503 0.997291 27.6719C1.23826 27.1988 1.65423 26.716 2.16331 26.2402C3.1863 25.2842 4.69544 24.2533 6.31077 23.2617C7.93168 22.2668 9.68514 21.297 11.2102 20.4658C11.7622 20.165 12.2814 19.8817 12.7541 19.624C10.4469 19.9068 8.14114 20.2033 5.83616 20.5098C5.83791 20.7056 5.7913 20.895 5.71897 21.044C5.54852 21.3948 5.12366 21.7836 4.55491 21.624L4.53147 21.6162C4.1922 21.5014 3.70855 21.3797 3.301 21.1865L3.29807 21.1846C3.19611 21.1352 3.0848 21.0769 2.97483 21.0069L2.9094 20.9658L2.86155 20.9043C2.64956 20.636 2.50725 20.3351 2.52366 20.0117C2.54073 19.6759 2.72247 19.4211 2.93772 19.2422C3.33728 18.9102 3.98655 18.7248 4.59983 18.6035C5.23896 18.4771 5.94736 18.4015 6.53831 18.3428C6.83772 18.3131 7.10327 18.2881 7.32542 18.2627C7.55496 18.2365 7.70561 18.2133 7.7844 18.1924L7.81174 18.1856L7.84006 18.1817C9.87377 17.9063 11.9172 17.6772 13.9446 17.4287L13.9533 17.4277C14.4179 17.3806 14.8829 17.3283 15.3489 17.2754C13.0405 14.5649 10.8536 11.8113 8.43674 9.24025C8.13382 8.95523 7.83614 8.65258 7.54417 8.35841C7.24757 8.05959 6.95469 7.76685 6.65354 7.49123C6.11004 6.99385 5.55537 6.5696 4.9426 6.28517C4.7194 6.37741 4.48098 6.37301 4.28245 6.30666C4.01446 6.217 3.73437 5.98857 3.66526 5.64064C3.60288 5.33479 3.72968 5.04978 3.9592 4.84474L3.96995 4.83595C4.40694 4.46882 4.95188 4.45978 5.46604 4.61037C5.97384 4.75912 6.52376 5.0802 7.0842 5.49416C8.21065 6.32621 9.50398 7.62644 10.7785 9.03908C12.0575 10.4566 13.3373 12.0097 14.4377 13.3623C15.1578 14.2475 15.7973 15.0389 16.3108 15.6563C16.373 14.8691 16.4382 14.0801 16.5071 13.2891V13.2793H16.508C16.9095 9.55095 17.469 5.76732 18.2473 1.92384L18.2483 1.91701C18.3868 1.2858 18.8131 0.784656 19.3664 0.766617Z"
                    fill="#E60000"
                    stroke="#E60000"
                    strokeWidth="0.919355"
                  />
                </svg>
              </div>

              {/* Description */}
              <p ref={bodyRef.ref} className="body-default animate-in-view">
              Our latest global CX report shows there&rsquo;s a widening gap between what brands promise and what 
              customers actually experience. An astounding 91% of brands fall short. To see how your brand 
              stacks up, and how you can close the gap, please reach out to our team.
              </p>

              {/* CTA Button */}
              <div ref={buttonRef.ref} className="flex justify-center md:justify-start animate-in-view">
                <CTAButton
                  href="https://www.havasxindex.com/"
                  showArrow={true}
                  className="text-lg body-sm"
                  onClick={() => {
                    trackEvent('hp_click', {
                      click_text: 'Explore the X Index',
                      click_url: 'https://www.havasxindex.com/',
                      page_path: pathname
                    });
                  }}
                >
                  Explore the X Index
                </CTAButton>
              </div>
            </div>
          </div>

    
        </div>
      </section>

      <ContactModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
      <style jsx>{`
        .x-index__grid {
          display: grid;
          gap: 0.75rem;
          align-items: start;
        }
        @media (min-width: 768px) {
          .x-index__grid {
            grid-template-columns: 1fr 1fr; /* 60/40 split */
            gap: 4.0625rem;
          }
        }
        @media (min-width: 1024px) {
          .x-index__grid {
            grid-template-columns: 3fr 2fr; /* 60/40 split */
          }
        }
        .x-index__image {
          position: relative;
          width: 100%;
          aspect-ratio: 672 / 492;
          border-radius: 1.5rem; /* rounded-3xl ≈ 1.5rem */
          overflow: hidden;
        }
        @keyframes xindex-draw {
          0% {
            stroke-dasharray: 400;
            stroke-dashoffset: 400;
            opacity: 0.6;
          }
          100% {
            stroke-dasharray: 400;
            stroke-dashoffset: 0;
            opacity: 1;
          }
        }
        .xindex-asterisk-path {
          animation: xindex-draw 3s ease-in-out forwards;
        }
      `}</style>
    </>
  );
}
